import {
  arrayOf,
  bool,
  func,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string
} from 'prop-types';

export const creditType = shape({
  activated_by_id: number,
  activation_time: string,
  created_at: string.isRequired,
  credit_type: shape({
    duration_days: number.isRequired,
    id: number.isRequired,
    session_settings: shape({
      max_attendees: number.isRequired,
      max_length_minutes: number,
      max_video_quality: number.isRequired,
      max_audio_quality: number.isRequired
    }).isRequired,
    name: string
  }).isRequired,
  expiration_time: string,
  id: number.isRequired,
  instance_id: number,
  project_id: number.isRequired,
  purchased_by_id: number,
  state: string.isRequired,
  updated_at: string.isRequired
});

export const project = shape({
  id: number.isRequired,
  name: string.isRequired
});

// TODO: Use Records to allow us to correctly use `isRequired` here:
// `overlaySettingsType` is used in `instanceType`, and the data is loaded asynchronously
export const overlaySettingsType = shape({
  burnin: shape({
    enabled: bool,
    font: shape({ size: oneOf(['small', 'medium', 'large']) }),
    position: shape({
      horizontal_placement: oneOf(['left', 'middle', 'right']),
      random: shape({
        interval_seconds: number,
        enabled: bool
      }),
      scrolling: shape({ horizontal: bool }),
      vertical_placement: oneOf(['top', 'middle', 'bottom'])
    }),
    template: string
  })
});

export const streamSettingsType = shape({
  audio_layout: oneOf(['stereo', '5.1']),
  audio_quality: number,
  inputChroma: oneOf(['422', '444']),
  decklink_input: oneOf(['sdi', 'hdmi']),
  dynamic_range: oneOf(['SDR', 'HDR']),
  input_bitdepth: number,
  output_bitdepth: number,
  video_quality: number
});

export const availableStreamSettingsType = shape({
  available_decklink_inputs: arrayOf(string),
  available_input_bitdepths: arrayOf(number),
  available_input_chroma_options: objectOf(arrayOf(string)),
  available_output_bitdepths: arrayOf(number),
  available_output_chroma_options: objectOf(arrayOf(string)),
  available_video_bitrates: object,
  available_audio_bitrates: object,
  available_audio_layouts: arrayOf(string),
  available_dynamic_ranges: arrayOf(string)
});

export const instanceType = shape({
  alias: string,
  credits: arrayOf(creditType),
  flexbox_id: string.isRequired,
  id: number.isRequired,
  in_use: bool,
  online: bool,
  overlay_settings: overlaySettingsType,
  streaming: bool,
  timestamp: string,
  stream_settings_defaults: streamSettingsType,
  available_stream_settings: availableStreamSettingsType
});

export const userType = shape({
  email: string.isRequired,
  first_name: string.isRequired,
  global_id: string,
  id: number.isRequired,
  last_name: string.isRequired,
  role: oneOf(['admin', 'presenter']).isRequired
});

export const locationType = shape({
  pathname: string.isRequired,
  search: string.isRequired
});

export const projectType = shape({
  created_at: string,
  credits: arrayOf(creditType),
  id: number,
  instances: arrayOf(instanceType),
  name: string,
  plan: shape({
    id: number.isRequired,
    is_contract: bool.isRequired,
    name: string.isRequired
  }),
  preferred_currency: oneOf(['gbp', 'usd', 'eur']),
  updated_at: string,
  users: arrayOf(userType)
});

export const attendeeType = shape({
  email: string
});

export const presenterType = shape({
  email: string.isRequired,
  last_seen: string,
  name: string.isRequired
});

export const sessionType = shape({
  attendees: arrayOf(attendeeType),
  end_time: string,
  flexbox_id: string.isRequired,
  instance_alias: string.isRequired,
  name: string.isRequired,
  presenter: presenterType.isRequired,
  project_id: number.isRequired,
  start_time: string.isRequired,
  streaming_session_id: string.isRequired,
  group: shape({
    id: number,
    name: string.isRequired
  })
});

export const paginationType = shape({
  num_pages: number,
  page_number: number,
  page_size: number,
  total_results: number
});

export const uiType = shape({
  error: shape({
    description: string.isRequired,
    errors: arrayOf(oneOfType([object, string])).isRequired,
    status: number
  }),
  loader: bool.isRequired
});

export const pageLinkType = shape({
  id: string.isRequired,
  text: string.isRequired,
  url: string.isRequired
});

export const defaultSelectOptionType = shape({
  label: string.isRequired,
  value: oneOfType([string, number]).isRequired
});

export const paymentMethodType = shape({
  brand: string.isRequired, // TODO: make more specific oneOf
  name: string.isRequired,
  last4: string.isRequired,
  country: string.isRequired,
  exp_month: number.isRequired,
  exp_year: number.isRequired,
  id: string.isRequired,
  metadata: object
});

export const skuType = shape({
  sku_id: string.isRequired,
  price: number.isRequired,
  currency: string.isRequired,
  name: string.isRequired,
  active: bool.isRequired
});

// https://stripe.com/docs/stripe-js/reference#the-stripe-object
export const stripeObjectType = shape({
  elements: func.isRequired,
  createToken: func.isRequired,
  createSource: func.isRequired,
  createPaymentMethod: func.isRequired,
  retrieveSource: func.isRequired,
  paymentRequest: func.isRequired,
  redirectToCheckout: func.isRequired,
  retrievePaymentIntent: func.isRequired,
  handleCardPayment: func.isRequired,
  handleCardAction: func.isRequired,
  confirmPaymentIntent: func.isRequired,
  handleCardSetup: func.isRequired,
  retrieveSetupIntent: func.isRequired,
  confirmSetupIntent: func.isRequired
});

export const burnInOptionsType = shape({
  fontSizeOptions: arrayOf(defaultSelectOptionType),
  horizontalPlacementOptions: arrayOf(defaultSelectOptionType),
  templateOptions: arrayOf(defaultSelectOptionType),
  verticalPlacementOptions: arrayOf(defaultSelectOptionType)
});

export const defaultsOptionsType = shape({
  audioChannelsOptions: arrayOf(string),
  audioQualityOptions: object,
  inputBitdepthOptions: arrayOf(number),
  inputChromaOptions: arrayOf(string),
  decklinkInputOptions: arrayOf(string),
  dynamicRangeOptions: arrayOf(string),
  outputBitdepthOptions: arrayOf(number),
  videoQualityOptions: object
});

export const sortConfigType = shape({
  key: string,
  sortAscending: bool
});

export const licencePlanType = shape({
  id: number,
  is_contract: bool,
  name: string
});

export const licenceType = shape({
  licence_number: string.isRequired,
  status: oneOf(['used', 'unused', 'provisioning', 'revoked']).isRequired,
  plan: licencePlanType.isRequired,
  flexbox_id: string,
  project_id: number
});

export const regionType = shape({
  id: number.isRequired,
  name: string.isRequired,
  readableName: string.isRequired
});

export const overflowBehaviourType = oneOf([
  'auto',
  'hidden',
  'inherit',
  'overlay',
  'unset',
  'revert',
  'scroll',
  'visible'
]);
